import React, { useState, useEffect } from "react";
import axios from "axios";
import { Eye, Users, BarChart3, Globe, FileText, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [statsRes, recentRes] = await Promise.all([
        axios.get(`${API}/analytics/stats`),
        axios.get(`${API}/analytics/recent?limit=15`)
      ]);
      setStats(statsRes.data);
      setRecentVisits(recentRes.data.visitas || []);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="analytics-loading">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Eye className="text-blue-400" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" data-testid="analytics-visits-today">{stats?.hoy?.visitas || 0}</p>
                <p className="text-xs text-slate-400">Visitas Hoy</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500/20 rounded-xl">
                <Users className="text-green-400" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" data-testid="analytics-unique-today">{stats?.hoy?.visitantes_unicos || 0}</p>
                <p className="text-xs text-slate-400">Visitantes Únicos Hoy</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <BarChart3 className="text-purple-400" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" data-testid="analytics-7d">{stats?.ultimos_7_dias?.total_visitas || 0}</p>
                <p className="text-xs text-slate-400">Últimos 7 días</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500/20 rounded-xl">
                <Globe className="text-amber-400" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" data-testid="analytics-30d-unique">{stats?.ultimos_30_dias?.visitantes_unicos || 0}</p>
                <p className="text-xs text-slate-400">Visitantes (30 días)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="glass border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText size={18} className="text-purple-400" />
              Páginas Más Visitadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.paginas_populares?.length > 0 ? (
                stats.paginas_populares.map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-slate-300 truncate max-w-[200px]">{page.page || "/"}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 rounded-full"
                          style={{ width: `${Math.min((page.visitas / (stats.paginas_populares[0]?.visitas || 1)) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-white font-medium w-8 text-right">{page.visitas}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">Sin datos aún</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock size={18} className="text-cyan-400" />
              Horas Más Activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-6 gap-1">
              {Array.from({ length: 24 }, (_, i) => {
                const hourData = stats?.horas_activas?.find(h => h.hora === i);
                const visits = hourData?.visitas || 0;
                const maxVisits = Math.max(...(stats?.horas_activas?.map(h => h.visitas) || [1]));
                const intensity = visits > 0 ? Math.max(0.2, visits / maxVisits) : 0;

                return (
                  <div 
                    key={i}
                    className="aspect-square rounded flex items-center justify-center text-xs"
                    style={{ 
                      backgroundColor: visits > 0 ? `rgba(147, 51, 234, ${intensity})` : 'rgba(51, 65, 85, 0.3)'
                    }}
                    title={`${i}:00 - ${visits} visitas`}
                  >
                    {i}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center">Hora del día (0-23)</p>
          </CardContent>
        </Card>
      </div>

      <Card className="glass border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye size={18} className="text-green-400" />
            Visitas Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {recentVisits.length > 0 ? (
              recentVisits.map((visit, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-slate-300">{visit.page}</span>
                  </div>
                  <span className="text-xs text-slate-500">
                    {new Date(visit.timestamp).toLocaleString()}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-4">Sin visitas recientes</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Button onClick={loadAnalytics} variant="outline" className="w-full" data-testid="analytics-refresh-btn">
        <Eye className="mr-2" size={16} />
        Actualizar Analíticas
      </Button>
    </div>
  );
};

export default AnalyticsTab;
